import { ChannelIcon } from '../../../../components/ui/ChannelIcon';
import type { CalendarRow } from '../../../../lib/publishing/calendarBuckets';
import { postStatusView } from '../../../../lib/socialPostView';
import { TONE } from './PostRow';

// One calendar line, matching the staff chip (CalendarRowChip): time, the channel
// glyph, the placement when it is not a plain feed post, and the CAPTION. The data
// was always on the row (CalendarRow carries post, platform, placementLabel); the
// club page was drawing the platform WORD and throwing the rest away.
//
// ⚠️ Colours are theme TOKENS (index.css --tone-*), each with a real dark value. A
// fixed bg-*-100 / text-*-900 pair has no dark variant and goes unreadable there.

export const EXTERNAL_CHIP_CLASS = 'border border-dashed border-tone-external-border bg-tone-external text-tone-external-foreground';

export function chipCaption(content: string): string {
  const t = content.trim();
  return t === '' ? '(no caption)' : t;
}

export type ChipHover = { row: CalendarRow; rect: DOMRect };

export function ClubCalendarChip({
  row,
  onOpen,
  onHover,
}: {
  row: CalendarRow;
  /** Click. For a published Kyck post the caller opens performance; otherwise it selects the day. */
  onOpen: () => void;
  onHover: (h: ChipHover | null) => void;
}) {
  const post = row.post;
  const external = post.origin === 'external';
  const tone = TONE[postStatusView(post.status, row.scheduledFor).tone];
  return (
    <button
      type="button"
      data-testid="calendar-chip"
      title={external ? 'Posted directly on the platform. Open the day to see it.' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onMouseEnter={(e) => onHover({ row, rect: e.currentTarget.getBoundingClientRect() })}
      onMouseLeave={() => onHover(null)}
      onFocus={(e) => onHover({ row, rect: e.currentTarget.getBoundingClientRect() })}
      onBlur={() => onHover(null)}
      className={`mt-0.5 flex w-full flex-col gap-0.5 rounded px-1 py-0.5 text-left text-[11px] ${external ? EXTERNAL_CHIP_CLASS : tone}`}
    >
      <span className="flex w-full items-center gap-1">
        {row.timeLabel && <span className="shrink-0 font-medium tabular-nums opacity-70">{row.timeLabel}</span>}
        {row.platform && <ChannelIcon channel={row.platform} className="h-3 w-3 shrink-0" />}
        {row.placementLabel && (
          <span className="shrink-0 rounded bg-background/60 px-1 text-[9px] font-semibold uppercase tracking-wide">{row.placementLabel}</span>
        )}
      </span>
      <span className="w-full truncate">{chipCaption(post.content)}</span>
    </button>
  );
}

/** Hover card: caption and the first image. Fixed at the chip's rect (the cell clips), and never takes the pointer. */
export function ClubChipHoverCard({ hover }: { hover: ChipHover }) {
  const WIDTH = 260;
  const { row, rect } = hover;
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - WIDTH - 8);
  const below = rect.bottom + 8;
  const fitsBelow = below + 300 < window.innerHeight;
  const style = fitsBelow ? { left, top: below } : { left, bottom: window.innerHeight - rect.top + 8 };
  const media = row.post.media[0];
  return (
    <div
      style={{ ...style, width: WIDTH }}
      className="pointer-events-none fixed z-40 overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-lg"
      data-testid="chip-hover"
    >
      {media && (
        <img
          src={media.thumbnailUrl ?? media.url}
          alt={media.altText ?? ''}
          className="max-h-56 w-full bg-muted object-contain"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <p className="line-clamp-6 whitespace-pre-wrap p-2 text-xs">{chipCaption(row.post.content)}</p>
    </div>
  );
}
